<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-chart-line me-2" style="color: #16a34a;"></i>{{ $t('Profit & Margin') }}
          </h5>
          <small class="text-muted">{{ $t('Finance & Payments') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
          <div class="vr mx-1 d-none d-md-block"></div>
          <span class="text-muted small">{{ $t('Group by') }}:</span>
          <div class="btn-group btn-group-sm" role="group">
            <button
              v-for="g in groupOptions"
              :key="g.value"
              type="button"
              class="btn"
              :class="filters.groupBy === g.value ? 'btn-primary' : 'btn-outline-secondary'"
              :disabled="loading"
              @click="setGroupBy(g.value)"
            >
              {{ $t(g.label) }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- KPI Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4 col-lg-3" v-for="kpi in profitKPIs" :key="kpi.key">
        <div class="card border-0 shadow-sm h-100 kpi-card" :style="kpiCardStyle(kpi)">
          <div class="card-body p-3">
            <div class="d-flex align-items-center gap-2 mb-2">
              <span class="kpi-icon" :style="{ backgroundColor: kpi.accent + '1f', color: kpi.accent }">
                <i :class="kpi.icon"></i>
              </span>
              <div class="text-muted small text-truncate">{{ $t(kpi.label) }}</div>
            </div>
            <div class="fw-bold fs-4 lh-1" :class="kpi.colorClass">{{ kpi.value }}</div>
            <div v-if="kpi.sub" class="small mt-2 text-truncate" :class="kpi.subClass || 'text-muted'">{{ kpi.sub }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Daily Profit Trend Chart -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-chart-area me-2 text-success"></i>{{ $t('Daily Profit Trend') }}</h6>
      </div>
      <div class="card-body pt-0">
        <apexchart ref="trendChart" type="area" height="300" :options="trendChartOptions" :series="trendChartSeries" />
      </div>
    </div>

    <!-- Operating Expense Breakdown -->
    <div v-if="expenseBreakdown.length" class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-receipt me-2 text-danger"></i>{{ $t('Operating Expense Breakdown') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Category') }}</th>
                <th class="text-end pe-3">{{ $t('Amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in expenseBreakdown" :key="item.category">
                <td class="ps-3">{{ $t(item.category) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(item.amount) }}</td>
              </tr>
            </tbody>
            <tfoot class="table-light fw-bold">
              <tr>
                <td class="ps-3">{{ $t('Total') }}</td>
                <td class="text-end pe-3 text-danger">{{ formatCurrency(summary.operating_expenses) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Grouped Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold">
          <i class="fa-solid fa-table me-2 text-secondary"></i>{{ $t('Profit by') }} {{ $t(activeGroupLabel) }}
        </h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t(activeGroupLabel) }}</th>
                <th v-if="filters.groupBy === 'product'">{{ $t('Category') }}</th>
                <th v-if="hasQtyColumn" class="text-end">{{ $t('Qty') }}</th>
                <th v-if="hasOrdersColumn" class="text-end">{{ $t('Orders') }}</th>
                <th class="text-end">{{ $t('Revenue') }}</th>
                <th class="text-end">{{ $t('Cost') }}</th>
                <th class="text-end">{{ $t('Profit') }}</th>
                <th class="text-end" :class="{ 'pe-3': !hasExpenseColumns }">{{ $t('Margin') }} %</th>
                <th v-if="hasExpenseColumns" class="text-end">{{ $t('Operating Expenses') }}</th>
                <th v-if="hasExpenseColumns" class="text-end pe-3">{{ $t('Net Profit') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length && !loading">
                <td :colspan="tableColspan" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="row in rows" :key="row.key">
                <td class="ps-3 fw-medium">{{ $t(row.label) }}</td>
                <td v-if="filters.groupBy === 'product'">
                  <span class="badge bg-light text-dark">{{ $t(row.category || '-') }}</span>
                </td>
                <td v-if="hasQtyColumn" class="text-end">{{ row.qty || 0 }}</td>
                <td v-if="hasOrdersColumn" class="text-end">{{ row.orders || 0 }}</td>
                <td class="text-end">{{ formatCurrency(row.revenue) }}</td>
                <td class="text-end text-muted">{{ formatCurrency(row.cost) }}</td>
                <td class="text-end fw-semibold" :class="profitClass(row.profit)">{{ formatCurrency(row.profit) }}</td>
                <td class="text-end" :class="[marginClass(row.margin), { 'pe-3': !hasExpenseColumns }]">{{ formatPercent(row.margin) }}</td>
                <td v-if="hasExpenseColumns" class="text-end text-danger">{{ formatCurrency(row.operating_expenses) }}</td>
                <td v-if="hasExpenseColumns" class="text-end pe-3 fw-semibold" :class="profitClass(row.net_profit)">{{ formatCurrency(row.net_profit) }}</td>
              </tr>
            </tbody>
            <tfoot v-if="rows.length" class="table-light fw-bold">
              <tr>
                <td class="ps-3">{{ $t('Total') }}</td>
                <td v-if="filters.groupBy === 'product'"></td>
                <td v-if="hasQtyColumn" class="text-end">{{ rowTotals.qty }}</td>
                <td v-if="hasOrdersColumn" class="text-end">{{ rowTotals.orders }}</td>
                <td class="text-end">{{ formatCurrency(rowTotals.revenue) }}</td>
                <td class="text-end">{{ formatCurrency(rowTotals.cost) }}</td>
                <td class="text-end" :class="profitClass(rowTotals.profit)">{{ formatCurrency(rowTotals.profit) }}</td>
                <td class="text-end" :class="[marginClass(rowTotals.margin), { 'pe-3': !hasExpenseColumns }]">{{ formatPercent(rowTotals.margin) }}</td>
                <td v-if="hasExpenseColumns" class="text-end text-danger">{{ formatCurrency(rowTotals.operating_expenses) }}</td>
                <td v-if="hasExpenseColumns" class="text-end pe-3" :class="profitClass(rowTotals.net_profit)">{{ formatCurrency(rowTotals.net_profit) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <!-- Loading spinner -->
    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportProfit',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Profit & Margin',
      summary: null,
      trend: [],
      rows: [],
      groupOptions: [
        { value: 'day',      label: 'Day' },
        { value: 'product',  label: 'Product' },
        { value: 'category', label: 'Category' },
        { value: 'branch',   label: 'Branch' },
      ],
    };
  },
  created() {
    // Mixin doesn't include groupBy; merge it into filters before first fetch.
    this.filters.groupBy = 'day';
  },
  methods: {
    buildFetchParams() {
      return {
        branch_id: this.filters.branch,
        start: this.formatDate(this.filters.date_range.startDate),
        end: this.formatDate(this.filters.date_range.endDate),
        group_by: this.filters.groupBy,
      };
    },
    setGroupBy(value) {
      if (this.filters.groupBy === value) return;
      this.filters.groupBy = value;
      this.loadReport();
    },
    async fetchReport() {
      const res = await Reports.profit(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.summary  = payload.summary || null;
      this.trend    = payload.trend   || [];
      this.rows     = payload.rows    || [];
    },

    formatPercent(v) {
      if (v == null) return '-';
      return `${Number(v).toFixed(1)}%`;
    },
    profitClass(v) {
      if (v == null) return '';
      if (v > 0) return 'text-success';
      if (v < 0) return 'text-danger';
      return '';
    },
    marginClass(v) {
      if (v == null) return '';
      if (v >= 30) return 'text-success';
      if (v >= 10) return 'text-warning';
      return 'text-danger';
    },
    kpiCardStyle(kpi) {
      const style = { borderTop: `3px solid ${kpi.accent}` };
      if (kpi.featured) {
        style.background = `linear-gradient(135deg, #ffffff 55%, ${kpi.accent}14)`;
      }
      return style;
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const chartImg = await this.getChartImage('trendChart');
      const k = this.profitKPIs;
      const kpiToColumn = (kpi) => ({
        text: [
          { text: this.$t(kpi.label) + '\n', style: 'kpiLabel' },
          { text: String(kpi.value) + (kpi.sub ? `\n${kpi.sub}` : ''), style: 'kpiValue' },
        ],
        width: '*',
      });
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Profit & Margin'), style: 'sectionTitle' },
        { columns: k.slice(0, 3).map(kpiToColumn), margin: [0, 0, 0, 8] },
        { columns: k.slice(3).map(kpiToColumn),    margin: [0, 0, 0, 16] },
      ];
      if (chartImg) {
        content.push(
          { text: this.$t('Daily Profit Trend'), style: 'sectionTitle' },
          { image: chartImg, width: 500, margin: [0, 0, 0, 12] },
        );
      }
      const headerRow = [
        { text: this.$t(this.activeGroupLabel), style: 'tableHeader' },
        ...(this.filters.groupBy === 'product' ? [{ text: this.$t('Category'), style: 'tableHeader' }] : []),
        ...(this.hasQtyColumn ? [{ text: this.$t('Qty'), style: 'tableHeader', alignment: 'right' }] : []),
        ...(this.hasOrdersColumn ? [{ text: this.$t('Orders'), style: 'tableHeader', alignment: 'right' }] : []),
        { text: this.$t('Revenue'), style: 'tableHeader', alignment: 'right' },
        { text: this.$t('Cost'), style: 'tableHeader', alignment: 'right' },
        { text: this.$t('Profit'), style: 'tableHeader', alignment: 'right' },
        { text: this.$t('Margin') + ' %', style: 'tableHeader', alignment: 'right' },
      ];
      const bodyRows = this.rows.map(r => [
        { text: r.label, style: 'tableCell' },
        ...(this.filters.groupBy === 'product' ? [{ text: r.category || '-', style: 'tableCell' }] : []),
        ...(this.hasQtyColumn ? [{ text: String(r.qty || 0), style: 'tableCell', alignment: 'right' }] : []),
        ...(this.hasOrdersColumn ? [{ text: String(r.orders || 0), style: 'tableCell', alignment: 'right' }] : []),
        { text: this.formatCurrencyRaw(r.revenue), style: 'tableCell', alignment: 'right' },
        { text: this.formatCurrencyRaw(r.cost), style: 'tableCell', alignment: 'right' },
        { text: this.formatCurrencyRaw(r.profit), style: 'tableCell', alignment: 'right' },
        { text: this.formatPercent(r.margin), style: 'tableCell', alignment: 'right' },
      ]);
      content.push(
        { text: this.$t('Profit by') + ' ' + this.$t(this.activeGroupLabel), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            body: [headerRow, ...bodyRows],
          },
          layout: 'lightHorizontalLines',
        },
      );
      return { content };
    },
  },
  computed: {
    activeGroupLabel() {
      return this.groupOptions.find(g => g.value === this.filters.groupBy)?.label || 'Day';
    },
    hasQtyColumn() {
      return this.filters.groupBy === 'product';
    },
    hasOrdersColumn() {
      return this.filters.groupBy === 'day' || this.filters.groupBy === 'branch';
    },
    tableColspan() {
      let n = 5;
      if (this.filters.groupBy === 'product') n += 1;
      if (this.hasQtyColumn) n += 1;
      if (this.hasOrdersColumn) n += 1;
      if (this.hasExpenseColumns) n += 2;
      return n;
    },
    profitKPIs() {
      const s = this.summary || {};
      const revenue = Number(s.revenue) || 0;
      const cost = Number(s.cost) || 0;
      const wasteCost = Number(s.waste_cost) || 0;
      const internalCost = Number(s.internal_consumption_cost) || 0;
      const profit = s.profit != null ? Number(s.profit) : (revenue - cost);
      const margin = s.margin != null ? Number(s.margin) : (revenue > 0 ? (profit / revenue * 100) : 0);
      const adjustedProfit = s.adjusted_profit != null ? Number(s.adjusted_profit) : (revenue - cost - wasteCost - internalCost);
      const adjustedMargin = s.adjusted_margin != null ? Number(s.adjusted_margin) : (revenue > 0 ? (adjustedProfit / revenue * 100) : 0);
      const opex = Number(s.operating_expenses) || 0;
      const netProfit = s.net_profit != null ? Number(s.net_profit) : (adjustedProfit - opex);
      const netMargin = s.net_margin != null ? Number(s.net_margin) : (revenue > 0 ? (netProfit / revenue * 100) : 0);
      return [
        { key: 'revenue',  label: 'Revenue',              value: this.formatCurrency(revenue),     sub: `${s.total_orders || 0} ${this.$t('orders')}`, colorClass: 'text-primary', icon: 'fa-solid fa-arrow-trend-up', accent: '#3b82f6' },
        { key: 'cost',     label: 'Cost (COGS)',          value: this.formatCurrency(cost),        sub: '', colorClass: 'text-body', icon: 'fa-solid fa-box', accent: '#64748b' },
        { key: 'waste',    label: 'Waste Cost',           value: this.formatCurrency(wasteCost),   sub: '', colorClass: wasteCost > 0 ? 'text-danger' : 'text-body', icon: 'fa-solid fa-trash-can', accent: '#f59e0b' },
        { key: 'internal', label: 'Internal Consumption', value: this.formatCurrency(internalCost), sub: '', colorClass: internalCost > 0 ? 'text-info' : 'text-body', icon: 'fa-solid fa-utensils', accent: '#06b6d4' },
        { key: 'profit',   label: 'Gross Profit',         value: this.formatCurrency(profit),      sub: `${this.$t('Adjusted')}: ${this.formatCurrency(adjustedProfit)}`, subClass: this.profitClass(adjustedProfit), colorClass: this.profitClass(profit), icon: 'fa-solid fa-sack-dollar', accent: '#16a34a', featured: true },
        { key: 'margin',   label: 'Margin',               value: this.formatPercent(margin),       sub: `${this.$t('Adjusted')}: ${this.formatPercent(adjustedMargin)}`, subClass: this.marginClass(adjustedMargin), colorClass: this.marginClass(margin), icon: 'fa-solid fa-percent', accent: '#8b5cf6' },
        { key: 'opex',     label: 'Operating Expenses',   value: this.formatCurrency(opex),        sub: '', colorClass: opex > 0 ? 'text-danger' : 'text-body', icon: 'fa-solid fa-receipt', accent: '#ef4444' },
        { key: 'net',      label: 'Net Profit',           value: this.formatCurrency(netProfit),   sub: `${this.$t('Margin')}: ${this.formatPercent(netMargin)}`, subClass: this.marginClass(netMargin), colorClass: this.profitClass(netProfit), icon: 'fa-solid fa-wallet', accent: '#059669', featured: true },
      ];
    },
    expenseBreakdown() {
      return (this.summary && this.summary.expense_breakdown) || [];
    },
    hasExpenseColumns() {
      return this.filters.groupBy === 'day' || this.filters.groupBy === 'branch';
    },
    rowTotals() {
      const t = this.rows.reduce((acc, r) => ({
        qty:     acc.qty     + (r.qty     || 0),
        orders:  acc.orders  + (r.orders  || 0),
        revenue: acc.revenue + (r.revenue || 0),
        cost:    acc.cost    + (r.cost    || 0),
        profit:  acc.profit  + (r.profit  || 0),
        operating_expenses: acc.operating_expenses + (r.operating_expenses || 0),
        net_profit:         acc.net_profit         + (r.net_profit         || 0),
      }), { qty: 0, orders: 0, revenue: 0, cost: 0, profit: 0, operating_expenses: 0, net_profit: 0 });
      t.margin = t.revenue > 0 ? (t.profit / t.revenue * 100) : 0;
      return t;
    },
    trendChartSeries() {
      return [
        { name: this.$t('Revenue'), data: this.trend.map(t => t.revenue || 0) },
        { name: this.$t('Cost'),    data: this.trend.map(t => t.cost    || 0) },
        { name: this.$t('Profit'),  data: this.trend.map(t => t.profit  || 0) },
      ];
    },
    trendChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        xaxis: { categories: this.trend.map(t => t.date) },
        yaxis: { labels: { formatter: v => this.formatCompact(v) } },
        colors: ['#3b82f6', '#94a3b8', '#16a34a'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'right' },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
        grid: { borderColor: '#f1f5f9' },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}

/* KPI cards */
.kpi-card {
  border-radius: 0.6rem;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1.25rem rgba(15, 23, 42, 0.12) !important;
}
.kpi-icon {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

/* Section cards + tables */
.card {
  border-radius: 0.6rem;
}
.card-header h6 {
  letter-spacing: 0.01em;
}
.table thead th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
  border-bottom: none;
}
.table tbody td,
.table tfoot td {
  font-size: 0.875rem;
}
.table tfoot td {
  border-top: 2px solid #e2e8f0;
}
</style>
